import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IFilter } from 'src/screens/ResourcePlanner/components/AssignFlow/components/FilterItems/FilterItem';
import { postFetch } from 'src/utils/fetcher';
import { POST_PROJECT_SEARCH_KEY } from './postProjectSearchAPI.constants';
import {
  TProjectSearchReq,
  TProjectSearchReqCustomer,
  TProjectSearchReqDepartment,
  TProjectSearchReqLegalEntity,
  TProjectSearchReqProjectManager,
  TProjectSearchReqProjectType,
  TProjectSearchRes,
} from './postProjectSearchAPI.types';
import { ASSIGN_TAKE_ITEMS } from '../../constants';

const POST_PROJECT_SEARCH_URL_PATH = '/api/v2/resource-planner/project-search';

const postProjectSearch = (reqBody: TProjectSearchReq): Promise<TProjectSearchRes> =>
  postFetch({
    path: POST_PROJECT_SEARCH_URL_PATH,
    key: POST_PROJECT_SEARCH_KEY,
    body: JSON.stringify(reqBody),
  });

interface IUsePostProjectSearch {
  query: string;
  customer?: IFilter;
  department?: IFilter;
  legalEntity?: IFilter;
  projectManager?: IFilter;
  projectType?: IFilter;
  take?: number;
  skip?: string;
}

export const usePostProjectSearch = ({
  customer: c,
  department: d,
  legalEntity: le,
  projectManager: pm,
  projectType: pt,
  query,
  take = ASSIGN_TAKE_ITEMS,
  skip,
}: IUsePostProjectSearch) => {
  const customer: TProjectSearchReqCustomer = c?.values?.length
    ? {
        CustomerIds: c.values.map(({ value }) => value).toString(),
        IsCustomerInclusive: c.isInclude,
      }
    : {
        CustomerIds: undefined,
        IsCustomerInclusive: undefined,
      };

  const department: TProjectSearchReqDepartment = d?.values?.length
    ? {
        DepartmentIds: d.values.map(({ value }) => value).toString(),
        IsDepartmentInclusive: d.isInclude,
      }
    : {
        DepartmentIds: undefined,
        IsDepartmentInclusive: undefined,
      };

  const legalEntity: TProjectSearchReqLegalEntity = le?.values?.length
    ? {
        LegalEntityIds: le.values.map(({ value }) => value).toString(),
        IsLegalEntityInclusive: le.isInclude,
      }
    : {
        LegalEntityIds: undefined,
        IsLegalEntityInclusive: undefined,
      };

  const projectManager: TProjectSearchReqProjectManager = pm?.values?.length
    ? {
        ProjectManagerIds: pm.values.map(({ value }) => value).toString(),
        IsProjectManagerInclusive: pm.isInclude,
      }
    : {
        ProjectManagerIds: undefined,
        IsProjectManagerInclusive: undefined,
      };

  const projectType: TProjectSearchReqProjectType = pt?.values?.length
    ? {
        ProjectTypeIds: pt.values.map(({ value }) => value).toString(),
        IsProjectTypeInclusive: pt.isInclude,
      }
    : {
        ProjectTypeIds: undefined,
        IsProjectTypeInclusive: undefined,
      };

  const reqBody: TProjectSearchReq = {
    SearchText: query.trim(),
    ...customer,
    ...department,
    ...legalEntity,
    ...projectManager,
    ...projectType,
    Take: take,
    Skip: skip,
  };

  const filteredReqBody = Object.keys(reqBody).reduce((a, b) => {
    const key = b as keyof TProjectSearchReq;
    if (reqBody[key] !== undefined && reqBody[key] !== null && reqBody[key] !== '') {
      return { ...a, [key]: reqBody[key] };
    }
    return a;
  }, {} as TProjectSearchReq);

  const {
    data: tempDate,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    ...queryResult
  } = useInfiniteQuery<TProjectSearchRes>({
    getNextPageParam: (lastPage) =>
      lastPage.items?.length === 0 || (lastPage.items?.length ?? 0) < take
        ? null
        : lastPage.nextSkip,
    queryKey: [POST_PROJECT_SEARCH_KEY, filteredReqBody],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await postProjectSearch({
        Skip: String((filteredReqBody?.Take ?? 0) * pageParam),
        ...filteredReqBody,
      });

      return {
        items: response.entities ?? [], // Assuming response is an array of items
        nextSkip:
          (response.entities?.length ?? 0) < (filteredReqBody?.Take ?? 0) ? null : pageParam + 1,
      };
    },
  });

  const observerRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (!observerRef.current || !hasNextPage || isFetchingNextPage) return () => {};

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const data = useMemo(
    () => tempDate?.pages?.flatMap((page) => page.items) ?? [],
    [tempDate?.pages],
  );

  return { data, fetchNextPage, hasNextPage, isFetchingNextPage, observerRef, ...queryResult };
};
